import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper';
import useMediaQuery from '../custom-hooks/useMediaQuery';
import { device } from '../../utils/styled-component-mixins';
import LocationCard from './LocationCardComponent';
import ButtonLink from '../shared/ButtonLinkComponent';
import WidgetIdWrapper from '../shared/WidgetIdWrapper';
import { useLocalization } from '../custom-hooks/useLocalization';
import { withLocalization } from '../shared/Context/LocalizationContext';
import 'swiper/css';
import 'swiper/css/pagination';
import useOnResize from '../custom-hooks/use-on-resize';
import { compose } from '../utils/compose';
import { withWidgetSpacingWrapper } from '../shared/WidgetSpacingWrapper';

const CareerLocationWrapperStyled = styled.div`
  .btn-wrapper {
    display: flex;
    justify-content: center;
    position: relative;
    z-index: 100;

    @media ${device.desktop} {
      margin-top: 4.3rem;
    }
  }

  .angled-background {
    &:after {
      content: '';
    }

    &:before {
      content: '';
    }

    background: linear-gradient(
      360deg,
      rgba(99, 120, 138, 0.3) 0%,
      rgba(99, 120, 138, 0.3) 0.01%,
      rgba(99, 120, 138, 0) 100%
    );

    @media ${device.desktop} {
      background: none;

      &:after {
        content: none;
      }

      &:before {
        content: none;
      }
    }
  }

  .careers-locations-description {
    font-size: 1.6rem;
    line-height: 2.4rem;
    margin-bottom: 4rem;
    position: relative;
    z-index: 100;

    @media ${device.desktop} {
      margin-bottom: 3rem;
    }

    > * {
      line-height: 2.4rem;
      text-align: center;

      @media ${device.desktop} {
        line-height: 3.2rem;
      }
    }
  }

  h2 {
    margin-bottom: 1.6rem;
    text-align: center;
  }

  .swiper {
    filter: drop-shadow(0px 16px 80px rgba(91, 110, 127, 0.25));
  }
`;

const LocationCardWrapperStyled = styled.div`
  display: flex;
  flex-direction: column;

  @media ${device.tablet} {
    column-gap: 1.6rem;
    flex-direction: row;
  }

  &.preload * {
    animation: none !important;
    transition: none !important;
  }

  & > a {
    --card-left-right-padding: 2.4rem;
    justify-content: center;
    overflow: hidden;
    position: relative;

    @media ${device.tablet} {
      flex: 1 0 calc(25% - 1.6rem);
    }
  }
`;

const CareersLocationsComponent = (props) => {
  const [isFirstRender, setIsFirstRender] = useState(true);
  const { items, properties } = props;
  const isDesktop = useMediaQuery(device.desktop);
  const childTitleHeightsCount = useRef(0);
  const featuredCardWidgetRef = useRef(null);
  const tallestTitleHeight = useRef(0);
  const { resourceStrings } = useLocalization();
  const linkText = resourceStrings.careerLocationsLinkText;

  const setInitialContentPosition = (card, titleHeightDifference) => {
    const elementToAnimate = card.querySelector('.content-wrapper');
    const content = card.children[0].children[0].children[0].children[0].children[1];
    let contentHeight =
      card.children[0].children[0].children[0].children[0].children[1].clientHeight;
    const contentStyle = getComputedStyle(content);
    const spaceBetweenTitleAndCta = 33;

    contentHeight += parseFloat(contentStyle.marginTop) + parseFloat(contentStyle.marginBottom);
    const bottomPositionInRem =
      (contentHeight - titleHeightDifference - spaceBetweenTitleAndCta) / 10;

    elementToAnimate.style.setProperty('--bottom-position', `-${Math.abs(bottomPositionInRem)}rem`);
  };

  const setInitialContentPositions = () => {
    const cards = featuredCardWidgetRef.current.querySelectorAll('a:not(.btn)');

    cards.forEach((card) => {
      const title = card.querySelector('h3');
      const titleStyles = getComputedStyle(title);
      const titleMarginBottom = parseInt(titleStyles.marginBottom, 10);
      const titleHeight = title.getBoundingClientRect().height + titleMarginBottom;
      const titleHeightDifference = tallestTitleHeight.current - titleHeight;
      setInitialContentPosition(card, titleHeightDifference);
    });
  };

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
    }

    if (
      document.readyState === 'complete' &&
      childTitleHeightsCount.current === props.items.length
    ) {
      setInitialContentPositions();
    } else {
      window.addEventListener('load', setInitialContentPositions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFirstRender]);

  const getTitleHeightCallBack = (height) => {
    childTitleHeightsCount.current += 1;
    if (height > tallestTitleHeight.current) {
      tallestTitleHeight.current = height;
    }

    if (childTitleHeightsCount.current === props.items.length) {
      setInitialContentPositions();
    }
  };

  useOnResize(() => setInitialContentPositions());

  return (
    <WidgetIdWrapper widgetID={properties.widgetID}>
      <CareerLocationWrapperStyled className="card-slider" ref={featuredCardWidgetRef}>
        <h2 className="container-padding-md">{properties.header}</h2>
        <div className="angled-background">
          <div className="container-padding-md">
            <div
              className="careers-locations-description"
              dangerouslySetInnerHTML={{ __html: properties.description }}
            ></div>
            {isDesktop && (
              <LocationCardWrapperStyled className={`${isFirstRender ? 'preload' : ''}`}>
                {items.map((item, index) => (
                  <LocationCard
                    getTitleHeightCallBack={getTitleHeightCallBack}
                    key={`${item.pageTitle} ${index}`}
                    linkText={linkText}
                    {...item}
                  />
                ))}
              </LocationCardWrapperStyled>
            )}
          </div>
          {!isDesktop && (
            <Swiper
              pagination={{ clickable: false }}
              centeredSlides={true}
              slidesPerView="auto"
              spaceBetween={16}
              modules={[Pagination]}
              init={false}
            >
              {items.map((item, index) => (
                <SwiperSlide key={index}>
                  <LocationCard
                    getTitleHeightCallBack={getTitleHeightCallBack}
                    key={`${item.pageTitle} ${index}`}
                    linkText={linkText}
                    {...item}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </div>
        {properties.linkText && properties.linkUrl && (
          <ButtonLink
            text={properties.linkText}
            link={properties.linkUrl}
            type="tertiary"
            theme="light"
            className="container-padding-md"
          />
        )}
      </CareerLocationWrapperStyled>
    </WidgetIdWrapper>
  );
};

export default compose(withWidgetSpacingWrapper, withLocalization)(CareersLocationsComponent);
